import React, { useState, useMemo, useEffect } from "react";
import NavBar from "../components/Navbar/NavBar";
import Footer from "../components/Footer";
import { useDocTitle } from "../components/CustomHook";
import productsJson from "./produtos.json";

const PLACEHOLDER_IMAGE =
  "https://placehold.co/600x400/94A3B8/FFFFFF?text=Imagem+em+breve";

// --- NOVAS CONSTANTES ---
const MD_BREAKPOINT = 768; // 768px (padrão do Tailwind para 'md')
const PRODUCTS_PER_PAGE_DEFAULT = 27; // Ex: 3 colunas * 9 linhas = 27 (para telas grandes)
const PRODUCTS_PER_PAGE_SM = 12; // Ex: 2 colunas * 6 linhas = 12 (para telas pequenas/médias)

const Products = () => {
  useDocTitle("Nautex - catálogo");

  // 1. ESTADO PARA O NÚMERO DE PRODUTOS POR PÁGINA
  const [productsPerPage, setProductsPerPage] = useState(
    PRODUCTS_PER_PAGE_DEFAULT
  );

  // 2. FUNÇÃO E HOOKS PARA DETECTAR O BREAKPOINT
  useEffect(() => {
    const mediaQuery = window.matchMedia(`(min-width: ${MD_BREAKPOINT}px)`);

    const handleMediaQuery = (e) => {
      if (e.matches) {
        setProductsPerPage(PRODUCTS_PER_PAGE_DEFAULT);
      } else {
        setProductsPerPage(PRODUCTS_PER_PAGE_SM);
      }
    };

    handleMediaQuery(mediaQuery);
    mediaQuery.addListener(handleMediaQuery);

    return () => mediaQuery.removeListener(handleMediaQuery);
  }, []);

  function replaceNewlinesInTopLevel(data) {
    if (!data) return data;
    if (Array.isArray(data))
      return data.map((item) => replaceNewlinesInTopLevel(item));
    if (typeof data === "object" && data !== null) {
      for (const key in data) {
        if (typeof data[key] === "string")
          data[key] = data[key].replace(/\n/g, "<br/>");
      }
    }
    return data;
  }

  const products = replaceNewlinesInTopLevel(productsJson);

  const categoryTree = {
    todos: [],
    helices: ["yamaha", "johnson", "mercury", "suzuki"],
    peças: ["juntas", "retentores", "rotores", "diversos"],
    acessorios: ["capas", "diversos"],
  };

  const [selectedCategory, setSelectedCategory] = useState("todos");
  const [selectedSubcategory, setSelectedSubcategory] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [imageIndex, setImageIndex] = useState(0);

  // ESTADO PARA O TERMO DE PESQUISA
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    document.body.style.overflow = selectedProduct ? "hidden" : "auto";
  }, [selectedProduct]);

  // Resetar página quando qualquer filtro ou pesquisa mudar
  useEffect(() => {
    setCurrentPage(1);
  }, [productsPerPage, selectedCategory, selectedSubcategory, searchTerm]);

  const filteredProducts = useMemo(() => {
    let filtered = products;

    // 1. FILTRAR PELO TERMO DE PESQUISA (Prioridade)
if (searchTerm) {
      // Normaliza e coloca o termo de pesquisa em minúsculo
      const normalizedSearchTerm = searchTerm
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");

      if (normalizedSearchTerm) {
        filtered = filtered.filter((p) => {
          
          // Função auxiliar inline para normalizar strings do produto
          const normalizeAndCompare = (text) => 
             text
              .toLowerCase()
              .normalize("NFD")
              .replace(/[\u0300-\u036f]/g, "")
              .includes(normalizedSearchTerm);

          const matchesNome = normalizeAndCompare(p.nome);
          const matchesCodigo = normalizeAndCompare(p.codigo);
          
          let matchesDescricao = false;
          if (p.descricao) {
             matchesDescricao = normalizeAndCompare(p.descricao);
          }

          return matchesNome || matchesCodigo || matchesDescricao;
        });
      }
    }

    // 2. FILTRAR POR CATEGORIA/SUBCATEGORIA
    if (selectedCategory !== "todos")
      filtered = filtered.filter((p) => p.categoria === selectedCategory);
    if (selectedSubcategory)
      filtered = filtered.filter((p) => p.subcategoria === selectedSubcategory);

    return filtered;
  }, [products, selectedCategory, selectedSubcategory, searchTerm]);

  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);
  const startIndex = (currentPage - 1) * productsPerPage;
  const paginatedProducts = filteredProducts.slice(
    startIndex,
    startIndex + productsPerPage
  );

  const formatLabel = (str) =>
    str.charAt(0).toUpperCase() + str.slice(1).replace(/-/g, " ");

  const handleOpenModal = (product) => {
    setSelectedProduct(product);
    setImageIndex(0);
  };

  const handleCloseModal = () => setSelectedProduct(null);

  const nextImage = (e) => {
    e.stopPropagation();
    if (selectedProduct?.images?.length > 1)
      setImageIndex((prev) => (prev + 1) % selectedProduct.images.length);
  };

  const prevImage = (e) => {
    e.stopPropagation();
    if (selectedProduct?.images?.length > 1)
      setImageIndex(
        (prev) =>
          (prev - 1 + selectedProduct.images.length) %
          selectedProduct.images.length
      );
  };

  return (
    <>
      <NavBar />
      <div className="my-32 px-5 md:px-12">
        <div className="flex gap-5 md:gap-10 flex-col md:flex-row">
          <div className="w-full md:w-1/4 gap-4 md:mb-10">
            <h3 className="text-xl font-bold text-blue-900 mb-5">PESQUISA</h3>

            {/* CAMPO DE PESQUISA */}
            <div className="mb-8">
              <input
                type="text"
                placeholder="Buscar por nome ou código..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-900 focus:border-blue-900 transition"
              />
            </div>

            <h3 className="text-xl font-bold text-blue-900 mb-5">CATEGORIAS</h3>
            <button
              onClick={() => {
                setSelectedCategory("todos");
                setSelectedSubcategory("");
                setCurrentPage(1);
              }}
              className={`w-full border-0 px-4 py-2 font-semibold text-left mb-2 ${
                selectedCategory === "todos"
                  ? "bg-blue-900 text-white"
                  : "bg-white text-blue-900 hover:bg-blue-900 hover:text-white"
              } transition`}
            >
              Todos
            </button>
            {Object.entries(categoryTree)
              .filter(([key]) => key !== "todos")
              .map(([cat, subs]) => (
                <div key={cat} className="mb-4">
                  <button
                    onClick={() => {
                      setSelectedCategory(cat);
                      setSelectedSubcategory("");
                      setCurrentPage(1);
                    }}
                    className={`w-full text-left font-bold px-4 py-2 border-0 ${
                      selectedCategory === cat && !selectedSubcategory
                        ? "bg-blue-900 text-white"
                        : "bg-white text-blue-900 hover:bg-blue-900 hover:text-white"
                    } transition`}
                  >
                    {formatLabel(cat)}
                  </button>
                  {selectedCategory === cat &&
                    subs.map((sub) => (
                      <button
                        key={sub}
                        onClick={() => {
                          setSelectedSubcategory(sub);
                          setCurrentPage(1);
                        }}
                        className={`block w-full pl-8 py-1 text-left font-medium ${
                          selectedSubcategory === sub
                            ? "text-blue-900 font-extrabold"
                            : "text-gray-700 hover:bg-blue-100"
                        } transition`}
                      >
                        {formatLabel(sub)}
                      </button>
                    ))}
                </div>
              ))}
          </div>

          {/* ======= LISTA DE PRODUTOS ======= */}
          <div className="w-full md:w-3/4">
            {filteredProducts.length === 0 ? (
              <p className="text-gray-600">
                Nenhum produto encontrado para o termo "{searchTerm}".
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {paginatedProducts.map((product) => (
                  <div
                    key={
                      product.numero + product.categoria + product.subcategoria
                    }
                    className="bg-white border border-gray-200 rounded-2xl shadow-md hover:shadow-xl transition cursor-pointer"
                    onClick={() => handleOpenModal(product)}
                  >
                    <img
                      src={
                        product.images?.length
                          ? `/images/produtos/${product.images[0]}`
                          : PLACEHOLDER_IMAGE
                      }
                      alt={product.nome}
                      className="w-full h-48 object-contain p-4"
                    />
                    <div className="p-4">
                      <h2 className="text-lg font-bold text-blue-900 mb-2">
                        {product.nome}
                      </h2>
                      <p
                        className="text-gray-600 text-sm"
                        dangerouslySetInnerHTML={{ __html: product.codigo }}
                      ></p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ======= PAGINAÇÃO ======= */}
            {totalPages > 1 && (
              <div className="flex md:justify-center md:items-center mt-8 gap-2 overflow-scroll">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1 rounded-md font-semibold ${
                        page === currentPage
                          ? "bg-blue-900 text-white"
                          : "bg-gray-200 text-blue-900 hover:bg-blue-900 hover:text-white"
                      } transition`}
                    >
                      {page}
                    </button>
                  )
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ======= MODAL ======= */}
      {selectedProduct && (
        <div
          className="fixed inset-0 bg-black/70 flex justify-center items-center z-50 p-6"
          onClick={handleCloseModal}
        >
          <div
            
            className="relative bg-white max-w-3xl w-full rounded-2xl shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleCloseModal}
              className="absolute top-3 right-3 z-[60] bg-blue-900 text-white w-8 h-8 rounded-full flex items-center justify-center hover:bg-white hover:text-blue-900 border border-blue-900 transition"
            >
              ✕
            </button>

            <div className="overflow-y-auto max-h-[70vh] lg:max-h-[90vh] p-6 pt-10 md:pt-6">
              
              <div className="relative flex flex-col items-center">
                
                <div className="relative w-full flex justify-center mb-4">
                  <img
                    src={
                      selectedProduct.images?.length
                        ? `/images/produtos/${selectedProduct.images[imageIndex]}`
                        : PLACEHOLDER_IMAGE
                    }
                    alt={selectedProduct.nome}
                    className="max-h-[400px] object-contain rounded-xl"
                  />
                  {selectedProduct.images?.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-3 top-1/2 -translate-y-1/2 hover:bg-blue-900/70 bg-blue-900 text-white hover:text-blue-900 rounded-full p-3 shadow-lg transition"
                      >
                        ❮
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-3 top-1/2 -translate-y-1/2 hover:bg-blue-900/70 bg-blue-900 text-white hover:text-blue-900 rounded-full p-3 shadow-lg transition"
                      >
                        ❯
                      </button>
                    </>
                  )}
                </div>

                <h2 className="text-2xl font-bold text-blue-900 mb-2">
                  {selectedProduct.nome}
                </h2>
                <p
                  className="text-gray-700 text-sm text-center"
                  dangerouslySetInnerHTML={{ __html: selectedProduct.codigo }}
                ></p>
                
                {selectedProduct.descricao && (
                  <p
                    className="text-gray-700 text-sm text-left mt-4"
                    dangerouslySetInnerHTML={{
                      __html: selectedProduct.descricao,
                    }}
                  ></p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      <Footer />
    </>
  );
};

export default Products;
